import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import {
  getBrowserLanguage,
  getFullBrowserLanguage,
  getLanguageDisplayName,
  isValidLanguage,
  changeLanguage,
  setTranslateAttribute,
  isBrowserTranslationActive,
  storeOriginalLanguage,
  createTranslationObserver,
  getLanguageFromCookie,
  getLanguageFromStorage,
} from '@/lib/language-service';
import { componentLogger } from '@/lib/logging';
import {
  getTranslationPreference,
  saveTranslationPreference,
  shouldShowTranslationBanner,
  isTranslationAllowedForCurrentLanguage,
  type TranslationChoice,
} from '@/lib/translation-preferences';

interface TranslationManagerState {
  // Banner state
  showBanner: boolean;
  browserLanguage: string | null;
  browserLanguageDisplayName: string;
  isProcessingChoice: boolean;

  // Warning badge state
  showWarningBadge: boolean;
  isTranslationActive: boolean;
  detectedTranslationLanguage: string | null;
  isDisablingTranslation: boolean;

  // General state
  isInitialized: boolean;
}

export function useTranslationManager() {
  const { t } = useTranslation();
  const observerRef = useRef<MutationObserver | null>(null);

  const [state, setState] = useState<TranslationManagerState>({
    showBanner: false,
    browserLanguage: null,
    browserLanguageDisplayName: '',
    isProcessingChoice: false,
    showWarningBadge: false,
    isTranslationActive: false,
    detectedTranslationLanguage: null,
    isDisablingTranslation: false,
    isInitialized: false,
  });

  /**
   * Handle existing user preference
   */
  const handleExistingPreference = useCallback(
    (preference: { choice: TranslationChoice; browserLanguage: string }, browserLang: string, browserLangDisplay: string) => {
      const choice = preference.choice as TranslationChoice;

      switch (choice) {
        case 'use_english':
          // Keep using English (default)
          setState(prev => ({
            ...prev,
            browserLanguage: browserLang,
            browserLanguageDisplayName: browserLangDisplay,
            isInitialized: true,
          }));
          break;

        default:
          // Use specific supported language
          if (isValidLanguage(choice)) {
            changeLanguage(choice, false);
            setState(prev => ({
              ...prev,
              browserLanguage: browserLang,
              browserLanguageDisplayName: browserLangDisplay,
              isInitialized: true,
            }));
          }
          break;
      }
    },
    []
  );

  /**
   * Initialize the translation management system
   */
  const initializeTranslationManager = useCallback(() => {
    try {
      // Store the original language for detection
      storeOriginalLanguage();

      // Get both supported and raw browser language information
      const browserLang = getBrowserLanguage(); // Only returns supported languages
      const fullBrowserLang = getFullBrowserLanguage(); // Returns any browser language
      const rawBrowserLang = fullBrowserLang?.split('-')[0]; // Get base language (e.g., 'cs' from 'cs-CZ')

      // If we have a raw browser language but no supported browser language, it's unsupported
      const isUnsupportedLanguage = rawBrowserLang && !browserLang;

      if (isUnsupportedLanguage) {
        // Check for existing saved language preference - don't clear it
        const savedLanguage = getLanguageFromCookie() || getLanguageFromStorage();
        if (savedLanguage) {
          // User has a saved preference, respect it and skip showing banner
          setState(prev => ({ ...prev, isInitialized: true }));
          return;
        }

        const browserLangDisplay = getLanguageDisplayName(rawBrowserLang);

        // Check existing preferences for this unsupported language
        const existingPreference = getTranslationPreference();

        if (existingPreference && existingPreference.browserLanguage === rawBrowserLang) {
          // User has already made a choice for this language
          handleExistingPreference(existingPreference, rawBrowserLang, browserLangDisplay);
        } else {
          // Show banner for new unsupported language
          const shouldShow = shouldShowTranslationBanner(rawBrowserLang);

          setState(prev => ({
            ...prev,
            browserLanguage: rawBrowserLang,
            browserLanguageDisplayName: browserLangDisplay,
            showBanner: shouldShow,
            isInitialized: true,
          }));
        }
        return;
      }

      if (!browserLang) {
        setState(prev => ({ ...prev, isInitialized: true }));
        return;
      }

      const browserLangDisplay = getLanguageDisplayName(browserLang);

      // Check for existing saved language preference first
      const savedLanguage = getLanguageFromCookie() || getLanguageFromStorage();
      if (savedLanguage && savedLanguage !== browserLang) {
        // User has a saved preference different from browser language - respect it
        componentLogger.debug('Respecting saved language preference over browser language', savedLanguage);
        setState(prev => ({
          ...prev,
          browserLanguage: browserLang,
          browserLanguageDisplayName: browserLangDisplay,
          isInitialized: true,
        }));
        return;
      }

      // Only auto-switch if no saved preference exists or it matches browser language
      if (!savedLanguage || savedLanguage === browserLang) {
        componentLogger.debug('Auto-switching to browser language (no saved preference)', browserLang);
        changeLanguage(browserLang, false); // Don't save to DB on auto-switch

        // Show toast only when actually auto-switching
        toast({
          description: t('translation.messages.usingBuiltIn', { language: browserLangDisplay })
        });
      }

      setState(prev => ({
        ...prev,
        browserLanguage: browserLang,
        browserLanguageDisplayName: browserLangDisplay,
        isInitialized: true,
      }));

    } catch (error) {
      componentLogger.error('[translation-manager]', 'Failed to initialize', error);
      setState(prev => ({ ...prev, isInitialized: true }));
    }
  }, [t, handleExistingPreference]);

  /**
   * Handle user choice from banner
   */
  const handleTranslationChoice = useCallback(
    async (choice: TranslationChoice) => {
      if (!state.browserLanguage) return;

      setState(prev => ({ ...prev, isProcessingChoice: true }));

      try {
        // Save the choice
        saveTranslationPreference(choice, state.browserLanguage);

        switch (choice) {
          case 'use_english':
            // Stay in English
            toast({
              description: t('translation.messages.choiceSaved')
            });
            setState(prev => ({ ...prev, showBanner: false, isProcessingChoice: false }));
            break;

          default:
            // Switch to specific supported language
            if (isValidLanguage(choice)) {
              const result = await changeLanguage(choice, false);
              if (result.success) {
                toast({
                  description: t('translation.messages.redirectingTo', { language: choice })
                });
                setState(prev => ({ ...prev, showBanner: false, isProcessingChoice: false }));
              } else {
                throw new Error(result.error || 'Failed to change language');
              }
            }
            break;
        }
      } catch (error) {
        componentLogger.error('[translation-manager]', 'Failed to process choice', error);
        toast({
          title: 'Error',
          description: t('translation.errors.preferenceNotSaved'),
          variant: 'destructive'
        });
        setState(prev => ({ ...prev, isProcessingChoice: false }));
      }
    },
    [state.browserLanguage, t]
  );

  /**
   * Close banner without making a choice
   */
  const closeBanner = useCallback(() => {
    setState(prev => ({ ...prev, showBanner: false }));
  }, []);

  /**
   * Handle disabling active browser translation
   */
  const disableTranslation = useCallback(async () => {
    setState(prev => ({ ...prev, isDisablingTranslation: true }));

    try {
      // Clear any existing translation preference that allows it
      if (state.browserLanguage) {
        saveTranslationPreference('use_english', state.browserLanguage);
      }

      // Re-enable translation blocking
      setTranslateAttribute(false);

      toast({
        description: 'Reloading page with translation disabled...'
      });

      // Reload page to remove translation
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      componentLogger.error('[translation-manager]', 'Failed to disable translation', error);
      toast({
        title: 'Error',
        description: t('translation.errors.translationFailed'),
        variant: 'destructive'
      });
      setState(prev => ({ ...prev, isDisablingTranslation: false }));
    }
  }, [state.browserLanguage, t]);

  /**
   * Set up translation detection observer
   */
  const setupTranslationObserver = useCallback(() => {
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Only set up observer if translation is allowed
    const isAllowed = state.browserLanguage &&
      isTranslationAllowedForCurrentLanguage(state.browserLanguage);

    if (!isAllowed) return;

    // Create new observer
    const observer = createTranslationObserver((isTranslated: boolean) => {
      setState(prev => ({
        ...prev,
        isTranslationActive: isTranslated,
        showWarningBadge: isTranslated,
        detectedTranslationLanguage: null, // Remove detectedLanguage parameter since it's not available
      }));
    });

    if (observer) {
      observerRef.current = observer;
    }

    // Also check current state
    const isCurrentlyTranslated = isBrowserTranslationActive();
    if (isCurrentlyTranslated) {
      setState(prev => ({
        ...prev,
        isTranslationActive: true,
        showWarningBadge: true,
      }));
    }
  }, [state.browserLanguage]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    if (!state.isInitialized) {
      initializeTranslationManager();
    }
  }, [state.isInitialized, initializeTranslationManager]);

  /**
   * Set up translation observer after initialization
   */
  useEffect(() => {
    if (state.isInitialized) {
      setupTranslationObserver();
    }

    // Cleanup observer on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [state.isInitialized, setupTranslationObserver]);

  return {
    // Banner state
    showBanner: state.showBanner,
    browserLanguage: state.browserLanguage,
    browserLanguageDisplayName: state.browserLanguageDisplayName,
    isProcessingChoice: state.isProcessingChoice,

    // Warning badge state
    showWarningBadge: state.showWarningBadge,
    isTranslationActive: state.isTranslationActive,
    detectedTranslationLanguage: state.detectedTranslationLanguage,
    isDisablingTranslation: state.isDisablingTranslation,

    // Actions
    handleTranslationChoice,
    closeBanner,
    disableTranslation,

    // Status
    isInitialized: state.isInitialized,
  };
}

export default useTranslationManager;